import React, { useEffect } from "react";
import {
  HiOutlineShieldCheck,
  HiOutlineLockClosed,
  HiOutlineDatabase,
  HiOutlineMail,
  HiOutlineLocationMarker,
  HiOutlineEye,
  HiOutlineKey,
  HiOutlineCalendar,
} from "react-icons/hi";
import { Link } from "react-router-dom";
import { scrollTop } from "../../utils/window";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";

const publicUrl = process.env.PUBLIC_URL || "";

function parsePrivacyIntro(prha1) {
  const match = prha1.match(/^Last updated on\s+([^.]+)\.\s*/i);
  if (!match) {
    return { lastUpdated: null, introText: prha1 };
  }
  return {
    lastUpdated: match[1].trim(),
    introText: prha1.slice(match[0].length).trim(),
  };
}

function parseDataItems(htmlCode) {
  return (htmlCode.match(/<li>(.*?)<\/li>/gi) || []).map((item) =>
    item.replace(/<\/?li>/gi, "").trim()
  );
}

function PrivacyPolicy() {
  const { feedback } = useContext(FeedbackContext);
  const privacy_plicy_page = feedback.privacy_plicy_page;
  const { lastUpdated, introText } = parsePrivacyIntro(privacy_plicy_page.prha1);
  const dataItems = parseDataItems(privacy_plicy_page.html_code);

  useEffect(() => {
    scrollTop();
  }, []);

  const tocItems = [
    { id: "privacy-intro", label: "Overview" },
    { id: "privacy-what-data-1", label: privacy_plicy_page.What_data1 },
    { id: "privacy-information", label: privacy_plicy_page.Information_provided },
    { id: "privacy-location", label: privacy_plicy_page.Location_information },
  ];

  const trustPoints = [
    { icon: HiOutlineLockClosed, label: "Encrypted", desc: "Data secured in transit" },
    { icon: HiOutlineEye, label: "Transparent", desc: "Clear privacy practices" },
    { icon: HiOutlineKey, label: "Your control", desc: "Rights over your data" },
  ];

  return (
    <div className="pi-info pi-privacy">
      <header className="pi-inner-hero pi-info__hero">
        <div className="pi-inner-hero__media" aria-hidden="true">
          <img
            className="pi-inner-hero__media-img pi-info__hero-img"
            src={`${publicUrl}/images/info-privacy-hero.jpg`}
            alt=""
          />
          <span className="pi-inner-hero__media-shade" />
        </div>
        <div className="pi-inner-hero__fx" aria-hidden="true">
          <span className="pi-inner-hero__glow pi-inner-hero__glow--a" />
          <span className="pi-inner-hero__glow pi-inner-hero__glow--b" />
          <span className="pi-inner-hero__grid" />
        </div>
        <div className="pi-inner-hero__content">
          <div className="pi-inner-hero__copy pi-info__reveal pi-info__reveal--1">
            <p className="pi-label pi-inner-hero__label">Legal</p>
            <h1 className="pi-inner-hero__title">{privacy_plicy_page.main_heading}</h1>
          </div>
        </div>
        <span className="pi-inner-hero__fade" aria-hidden />
      </header>

      <div className="pi-info__body">
        <div className="pi-info__body-fx" aria-hidden="true">
          <span className="pi-info__body-glow pi-info__body-glow--a" />
          <span className="pi-info__body-glow pi-info__body-glow--b" />
        </div>
        <div className="pi-info__inner">
          <div className="pi-info__layout">
            <main className="pi-info__main pi-privacy__main pi-info__reveal pi-info__reveal--2">
              {lastUpdated && (
                <div className="pi-privacy__updated" id="privacy-intro">
                  <span className="pi-privacy__updated-icon" aria-hidden>
                    <HiOutlineCalendar size={22} />
                  </span>
                  <div className="pi-privacy__updated-copy">
                    <span className="pi-privacy__updated-label">Last updated</span>
                    <time className="pi-privacy__updated-date" dateTime={lastUpdated}>
                      {lastUpdated}
                    </time>
                  </div>
                </div>
              )}

              <article className="pi-privacy__intro" id={lastUpdated ? undefined : "privacy-intro"}>
                <span className="pi-privacy__intro-accent" aria-hidden="true" />
                <div className="pi-privacy__intro-icon" aria-hidden>
                  <HiOutlineShieldCheck size={22} />
                </div>
                <div className="pi-privacy__intro-copy">
                  <span className="pi-privacy__badge">Privacy commitment</span>
                  <p>{introText}</p>
                </div>
              </article>

              <div className="pi-privacy__trust" aria-label="Privacy principles">
                {trustPoints.map((point) => {
                  const Icon = point.icon;
                  return (
                    <div key={point.label} className="pi-privacy__trust-item">
                      <div className="pi-privacy__trust-icon" aria-hidden>
                        <Icon size={18} />
                      </div>
                      <div className="pi-privacy__trust-copy">
                        <strong>{point.label}</strong>
                        <span className="pi-privacy__trust-desc">{point.desc}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <section id="privacy-what-data-1" className="pi-privacy__section">
                <header className="pi-privacy__section-head">
                  <span className="pi-privacy__section-num" aria-hidden>01</span>
                  <span className="pi-privacy__section-icon" aria-hidden>
                    <HiOutlineDatabase size={20} />
                  </span>
                  <h2>{privacy_plicy_page.What_data1}</h2>
                </header>
                <div className="pi-privacy__section-body">
                  <h3 id="privacy-what-data-2">{privacy_plicy_page.What_data2}</h3>
                </div>
              </section>

              <section id="privacy-information" className="pi-privacy__section">
                <header className="pi-privacy__section-head">
                  <span className="pi-privacy__section-num" aria-hidden>02</span>
                  <span className="pi-privacy__section-icon" aria-hidden>
                    <HiOutlineMail size={20} />
                  </span>
                  <h2>{privacy_plicy_page.Information_provided}</h2>
                </header>
                <div className="pi-privacy__section-body">
                  <ul className="pi-privacy__data-list">
                    {dataItems.map((item) => (
                      <li key={item} className="pi-privacy__data-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section id="privacy-location" className="pi-privacy__section pi-privacy__section--location">
                <header className="pi-privacy__section-head">
                  <span className="pi-privacy__section-num" aria-hidden>03</span>
                  <span className="pi-privacy__section-icon" aria-hidden>
                    <HiOutlineLocationMarker size={20} />
                  </span>
                  <h2>{privacy_plicy_page.Location_information}</h2>
                </header>
                <div className="pi-privacy__section-body">
                  <div className="pi-privacy__location-visual" aria-hidden="true">
                    <span className="pi-privacy__location-ring pi-privacy__location-ring--a" />
                    <span className="pi-privacy__location-ring pi-privacy__location-ring--b" />
                    <span className="pi-privacy__location-pin">
                      <HiOutlineLocationMarker size={22} />
                    </span>
                  </div>
                  <p>{privacy_plicy_page.prha2}</p>
                </div>
              </section>
            </main>

            <aside className="pi-info__aside pi-info__reveal pi-info__reveal--3">
              <div className="pi-info__art">
                <span className="pi-info__art-grid" aria-hidden />
                <span className="pi-info__art-icon" aria-hidden>
                  <HiOutlineShieldCheck size={24} />
                </span>
                <h3>Your data, protected</h3>
                <p>We handle your information with care and transparency at every step.</p>
                <div className="pi-info__art-chips">
                  <span className="pi-info__art-chip">Encrypted</span>
                  <span className="pi-info__art-chip">Transparent</span>
                </div>
              </div>

              <nav className="pi-privacy__toc pi-info__card" aria-label="On this page">
                <p className="pi-label pi-privacy__toc-label">On this page</p>
                <ul className="pi-privacy__toc-list">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`}>{item.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="pi-privacy__help pi-info__card">
                <span className="pi-privacy__help-icon" aria-hidden>
                  <HiOutlineLockClosed size={20} />
                </span>
                <p>Questions about how we use your data? Our team is happy to help.</p>
                <Link to="/contactus" className="pi-info__cta-link pi-privacy__help-link">
                  Contact us
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
